import React, { useRef, useImperativeHandle, useEffect } from "react";
import styles from "./button.module.scss";

type ButtonSolidThemes = "red500" | "red0" | "gray50" | "brown0" | "brown500" | "gray900";
type ButtonOutlineThemes = "gray900" | "gray200" | "brown100" | "brown500";
type ButtonTextThemes = "mint500" | "brown500";
type ButtonCircleThemes = "red500" | "red500-fixed";
type ButtonTypes = "solid" | "outline" | "text" | "circle" | "icon" | "";
type ButtonThemes<T> = T extends "solid"
  ? ButtonSolidThemes
  : T extends "outline"
    ? ButtonOutlineThemes
    : T extends "text"
      ? ButtonTextThemes
      : T extends "circle"
        ? ButtonCircleThemes
        : never;

export interface IButtonProps<T extends ButtonTypes> {
  size?: "tiny" | "small" | "medium" | "large" | "giant" | "";
  type?: T;
  theme?: ButtonThemes<T>;
  text?: string;
  fullWidth?: boolean;
  children?: React.ReactNode;
  autoWidth?: boolean;
  autoHeight?: boolean;
  transparent?: "all" | "bg" | "border";
  hasPaddingPosition?: "x" | "y" | "all";
  noPaddingPosition?: "x" | "y" | "all" | "top" | "bottom" | "left" | "right";
  isRound?: boolean;
  isBold?: boolean;
  columnGapValue?: number;
  color?: string;
  backgroundColor?: string;
  [key: string]: any;
}

/**
 * Button
 *
 * @param ref button ref
 * @param [key:string] any
 */

export const ButtonBox = React.memo(
  React.forwardRef<HTMLDivElement, IButtonProps<ButtonTypes>>(
    (
      {
        type = "",
        size = "",
        theme = "",
        isRound = false,
        text,
        fullWidth = false,
        children,
        autoWidth,
        autoHeight,
        transparent,
        hasPaddingPosition,
        noPaddingPosition,
        isBold,
        columnGapValue,
        color,
        backgroundColor,
        ...otherProps
      },
      ref,
    ) => {
      // button ref
      const buttonRef = useRef<HTMLDivElement | null>(null);
      // 전달된 ref가 있을 경우 buttonRef와 연결
      useImperativeHandle(ref, () => {
        if (buttonRef.current === null) {
          throw new Error("buttonRef is not assigned yet");
        }
        return buttonRef.current;
      });

      return (
        <div
          ref={buttonRef}
          {...otherProps}
          className={`${styles["btn"]} 
					${styles[size ? "btn-" + size : ""]} 
					${styles[isRound ? "btn-round" : ""]}
					${isBold ? styles["btn-bold"] : ""}
					${fullWidth ? styles["btn-full"] : ""} 
					${styles[`btn${type ? (type === "solid" ? "" : "-" + type) : ""}${theme ? "-" + theme : ""}`]} 
					${autoWidth ? styles["btn-auto-width"] : ""}
					${autoHeight ? styles["btn-auto-height"] : ""}
					${transparent ? styles["btn-transparent-" + transparent] : ""}
					${hasPaddingPosition ? styles["btn-has-padding-" + hasPaddingPosition] : ""}
					${noPaddingPosition ? styles["btn-no-padding-" + noPaddingPosition] : ""}
					${otherProps?.disabled ? styles["btn-disabled"] : ""}
					${otherProps.className ? otherProps.className : ""}`}
          style={
            otherProps.style
              ? {
                  ...otherProps?.style,
                  color: color ? color : otherProps?.style?.color || "",
                  backgroundColor: backgroundColor
                    ? backgroundColor
                    : otherProps?.style?.backgroundColor || "",
                  columnGap: columnGapValue !== undefined ? columnGapValue + "px" : "",
                }
              : {
                  color: color ? color : "",
                  backgroundColor: backgroundColor ? backgroundColor : "",
                  columnGap: columnGapValue !== undefined ? columnGapValue + "px" : "",
                }
          }
        >
          {text && text}
          {children && children}
        </div>
      );
    },
  ),
);

export const ButtonGroup = ({
  columns = 2,
  children,
  className,
  style,
}: {
  columns?: number;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => {
  return (
    <div
      className={`${styles["btn-group"]} ${styles["btn-group-colums-" + columns]} ${className ? className : ""}`}
      style={style ? style : {}}
    >
      {children}
    </div>
  );
};

export const ButtonWrap = ({
  children,
  firstRatio,
  secondRatio,
  className,
  style,
  noPaddingPosition,
  columnGapValue,
}: {
  children: React.ReactNode;
  firstRatio?: number | null;
  secondRatio?: number;
  className?: string;
  style?: React.CSSProperties;
  noPaddingPosition?: "x" | "y" | "all" | "top" | "bottom" | "left" | "right";
  columnGapValue?: number;
}) => {
  return (
    <div
      className={`${styles["btn-wrap"]} 
			${firstRatio ? styles["btn-wrap-ratio-" + firstRatio + "-" + (secondRatio ? secondRatio : firstRatio)] : ""} 
			${noPaddingPosition ? styles["btn-wrap-no-padding-" + noPaddingPosition] : ""}
			${columnGapValue ? styles["btn-wrap-column-gap" + columnGapValue] : ""}
			${className ? className : ""}`}
      style={style ? style : {}}
    >
      {children}
    </div>
  );
};
